import { subscribe, useSnapshot } from 'valtio'

import {
  floorState,
  switchToPanel,
  switchToSideBySide,
  switchToSolo,
  switchToTray,
  switchToCustomMix,
} from '../../firebase/hooks/useFloor'
import { mixesState } from '../../firebase/hooks/useMixes'
import { panelState, savePanelState } from '../../firebase/hooks/usePanel'
import { roomState } from '../../webrtc/webRTCRoom'

import {
  NOTE_ON,
  NOTE_OFF,
  MACRO_NOTE_ON,
  MACRO_NOTE_OFF,
  CONTROL_CHANGE,
  RECORD_CC,
  CANCEL_CC,
  VELOCITY_ON,
  VELOCITY_OFF,
  noteToSelector,
  selectorToNote,
  noteToMacroSlot,
  type SelectorKey,
} from './protocol'
import {
  midiState,
  loadMacros,
  saveMacros,
  getSortedHumanParticipants,
  speakerIdxToParticipantId,
  participantIdToSpeakerIdx,
  isSpeakerAvailable,
  isScreenAvailable,
  isSelectorAvailable,
  type Selection,
} from './state'

// ====== Constants ====== //

// window in which an exact echo of a light command is swallowed (once)
const ECHO_COOLDOWN_MS = 150
// debounce for applying multi-press selections, avoids ms-level layout churn
const SELECTION_DEBOUNCE_MS = 150

// ====== MIDI plumbing ====== //

let midiAccess: MIDIAccess | null = null
const unsubscribers: (() => void)[] = []
// messages we've just sent, to ignore echoes: key -> timestamp
const recentlySent = new Map<string, number>()
let selectionDebounceTimer: ReturnType<typeof setTimeout> | undefined
// accumulates selectors for the current "gesture" (until all keys released)
const selectionBuffer = new Set<SelectorKey>()

const messageKey = (status: number, data1: number, data2: number) =>
  `${status}-${data1}-${data2}`

const sendToOutputs = (status: number, data1: number, data2: number) => {
  if (!midiAccess) return
  recentlySent.set(messageKey(status, data1, data2), performance.now())
  midiAccess.outputs.forEach((output) => {
    output.send([status, data1, data2])
  })
}

/**
 * All MIDI in/out goes to the same device, so a light command may be echoed
 * back verbatim. Swallow an exact echo once within a short window — a
 * legitimate press at the same time produces a second identical message,
 * which then passes through.
 */
const consumeEcho = (status: number, data1: number, data2: number) => {
  const key = messageKey(status, data1, data2)
  const sentAt = recentlySent.get(key)
  if (sentAt === undefined) return false
  recentlySent.delete(key) // only ignore once
  return performance.now() - sentAt < ECHO_COOLDOWN_MS
}

const toggleMidiControls = () => {
  midiState.enabled = !midiState.enabled

  if (midiState.enabled) {
    void initializeMidi()
  } else {
    cleanupMidi()
  }
}

const initializeMidi = async () => {
  try {
    midiAccess = await navigator.requestMIDIAccess({ sysex: false })
  } catch (err) {
    console.error('MIDI access denied', err)
    midiState.enabled = false
    return
  }

  loadMacros()

  const attachInputs = () => {
    midiAccess?.inputs.forEach((input) => {
      // remove first so device reconnects don't double-register
      input.removeEventListener('midimessage', onMidiInput)
      input.addEventListener('midimessage', onMidiInput)
    })
  }
  attachInputs()
  midiAccess.onstatechange = attachInputs

  unsubscribers.push(subscribe(midiState.lights, syncLightsMidi))
  unsubscribers.push(subscribe(floorState, updateLightsState))
  unsubscribers.push(subscribe(panelState, updateLightsState))
  unsubscribers.push(subscribe(roomState, updateLightsState))
  unsubscribers.push(subscribe(midiState.recordedSelection, updateLightsState))

  updateLightsState()
}

const cleanupMidi = () => {
  unsubscribers.forEach((unsub) => unsub())
  unsubscribers.length = 0

  clearTimeout(selectionDebounceTimer)
  selectionDebounceTimer = undefined
  selectionBuffer.clear()

  // turn off all lights that are currently on
  Object.entries(midiState.lights).forEach(([key, on]) => {
    if (!on) return
    const note = selectorToNote(key)
    if (note !== undefined) sendToOutputs(NOTE_ON, note, VELOCITY_OFF)
  })

  midiAccess?.inputs.forEach((input) => {
    input.removeEventListener('midimessage', onMidiInput)
  })
  if (midiAccess) midiAccess.onstatechange = null
  midiAccess = null

  midiState.lights = {}
  midiState.pressedSelectors.clear()
  midiState.pressedMacros.clear()
  midiState.recordedSelection.clear()
  midiState.recording = false
  midiState.recordingTouched = false
  recentlySent.clear()
}

// ====== Applying a selection to the floor ====== //

/** Resolves index based selector keys into participant ids. */
const resolveSelectors = (selectors: ReadonlySet<SelectorKey>): Selection => {
  const selection: Selection = { speakerIds: [], screenIds: [], tray: false }

  selectors.forEach((sel) => {
    if (sel === 'tray') {
      selection.tray = true
      return
    }
    if (sel.startsWith('speaker-')) {
      const id = speakerIdxToParticipantId(Number(sel.slice(8)))
      if (id !== undefined) selection.speakerIds.push(id)
      return
    }
    if (sel.startsWith('screen-')) {
      const id = speakerIdxToParticipantId(Number(sel.slice(7)))
      if (id !== undefined && isScreenAvailable(id)) {
        selection.screenIds.push(id)
      }
    }
  })

  return selection
}

/**
 * Applies a selection of participant ids to the floor. Media that is no
 * longer in the room (participants that left, screens that stopped sharing)
 * is dropped, gracefully falling back to the available subset.
 */
const applySelection = (selection: Selection) => {
  const speakerIds = selection.speakerIds.filter(isSpeakerAvailable)
  const screenIds = selection.screenIds.filter(isScreenAvailable)
  const { tray } = selection

  // single selector cases
  if (speakerIds.length === 1 && screenIds.length === 0 && !tray) {
    void switchToSolo(speakerIds[0], 'camera')
    return
  }
  if (screenIds.length === 1 && speakerIds.length === 0 && !tray) {
    void switchToSolo(screenIds[0], 'screenshare')
    return
  }
  if (tray && speakerIds.length === 0 && screenIds.length === 0) {
    void switchToTray()
    return
  }

  // one speaker + their own screen -> side by side
  if (
    speakerIds.length === 1 &&
    screenIds.length === 1 &&
    speakerIds[0] === screenIds[0] &&
    !tray
  ) {
    void switchToSideBySide(speakerIds[0], 'screenshare')
    return
  }

  // everything else (including an intentionally empty selection) -> panel
  void savePanelState(speakerIds, tray ? 'tray' : screenIds[0]).then(() => {
    switchToPanel()
  })
}

const scheduleApplySelection = () => {
  clearTimeout(selectionDebounceTimer)
  selectionDebounceTimer = setTimeout(() => {
    const selection = resolveSelectors(selectionBuffer)
    // if the pressed keys resolve to nothing (e.g. a screen that isn't
    // sharing), do nothing instead of clearing the floor
    if (
      selection.speakerIds.length === 0 &&
      selection.screenIds.length === 0 &&
      !selection.tray
    ) {
      return
    }
    applySelection(selection)
  }, SELECTION_DEBOUNCE_MS)
}

// ====== Macros ====== //

const triggerMacroSlot = (slot: number) => {
  const stored = midiState.macros[slot]
  if (stored) {
    if (stored.type === 'selection') {
      applySelection(stored.selection)
      return
    }

    // floor snapshot macro — fall back gracefully if media left the room
    const { floor } = stored
    switch (floor.layout) {
      case 'panel': {
        const participantIds = (stored.panel?.participantIds ?? []).filter(
          isSpeakerAvailable
        )
        const screen = stored.panel?.screen
        const validScreen =
          screen === 'tray' ||
          (screen !== undefined && isScreenAvailable(screen))
            ? screen
            : undefined
        void savePanelState(participantIds, validScreen).then(() => {
          switchToPanel()
        })
        return
      }
      case 'custom':
        if (mixesState.mixes.some((m) => m.id === floor.mixId))
          void switchToCustomMix(floor.mixId)
        return
      case 'solo':
        if (
          floor.identifier === 'screenshare'
            ? isScreenAvailable(floor.participantId)
            : isSpeakerAvailable(floor.participantId)
        )
          void switchToSolo(floor.participantId, floor.identifier)
        return
      case 'side-by-side':
        if (isSpeakerAvailable(floor.participantId))
          void switchToSideBySide(
            floor.participantId,
            floor.identifier ?? 'screenshare'
          )
        return
      case 'tray':
        void switchToTray()
        return
    }
  }

  // defaults: slot 0 -> panel, slots 1–15 -> custom mixes 1–15
  if (slot === 0) {
    switchToPanel()
    return
  }
  const mix = mixesState.mixes.at(slot - 1)
  if (mix) void switchToCustomMix(mix.id)
}

const exitRecording = (apply: boolean) => {
  midiState.recording = false

  if (apply) {
    const heldMacro = midiState.pressedMacros.values().next()
    const heldSlot: number | undefined = heldMacro.done
      ? undefined
      : heldMacro.value

    if (heldSlot !== undefined) {
      if (midiState.recordingTouched) {
        // selection was made -> store it (as participant ids) on the pad.
        // An empty selection is valid — triggering it gives an empty panel.
        midiState.macros[heldSlot] = {
          type: 'selection',
          selection: resolveSelectors(midiState.recordedSelection),
        }
        saveMacros()
      } else if (floorState.floor) {
        // selection untouched -> store the current floor state, keeping
        // custom mixes as references instead of frozen selections
        midiState.macros[heldSlot] = {
          type: 'floor',
          floor: { ...floorState.floor },
          panel:
            floorState.floor.layout === 'panel'
              ? {
                  participantIds: [...panelState.participantIds],
                  screen: panelState.screen,
                }
              : undefined,
        }
        saveMacros()
      }
    } else if (midiState.recordingTouched) {
      // no pad held -> apply the selection to the floor
      applySelection(resolveSelectors(midiState.recordedSelection))
    }
    // no pad held, selection untouched -> floor already shows it, no-op
  }

  midiState.recordedSelection.clear()
  midiState.recordingTouched = false
  updateLightsState()
}

// ====== Input handling ====== //

const onMidiInput = (event: MIDIMessageEvent) => {
  const { data } = event
  if (!data || data.length < 3) return

  const [status, data1, data2] = data

  // ignore the (single) echo of our own light commands
  if (consumeEcho(status, data1, data2)) return

  // --- control change: record / cancel ---
  if (status === CONTROL_CHANGE) {
    if (data1 === RECORD_CC) {
      if (data2 === VELOCITY_ON) {
        midiState.recording = true
        midiState.recordingTouched = false
        // start from what's currently on screen
        midiState.recordedSelection.clear()
        computeFloorSelectors().forEach((sel) => {
          midiState.recordedSelection.add(sel)
        })
        updateLightsState()
      } else if (data2 === VELOCITY_OFF && midiState.recording) {
        exitRecording(true)
      }
      return
    }
    if (data1 === CANCEL_CC && data2 === VELOCITY_ON && midiState.recording) {
      exitRecording(false)
      return
    }
    return
  }

  // --- selector notes (channel 1) ---
  if (status === NOTE_ON || status === NOTE_OFF) {
    const selector = noteToSelector(data1)
    if (!selector) return

    const isPress = status === NOTE_ON && data2 > 0

    if (midiState.recording) {
      if (isPress && isSelectorAvailable(selector)) {
        // toggle in the local recorded selection, don't touch the floor
        if (midiState.recordedSelection.has(selector)) {
          midiState.recordedSelection.delete(selector)
        } else {
          midiState.recordedSelection.add(selector)
        }
        midiState.recordingTouched = true
      }
      return
    }

    if (isPress) {
      // a fresh gesture starts once all keys were released
      if (midiState.pressedSelectors.size === 0) selectionBuffer.clear()
      midiState.pressedSelectors.add(selector)
      selectionBuffer.add(selector)
      scheduleApplySelection()
    } else {
      // note-off or note-on with velocity 0
      midiState.pressedSelectors.delete(selector)
    }
    return
  }

  // --- macro pads (channel 10) ---
  if (status === MACRO_NOTE_ON || status === MACRO_NOTE_OFF) {
    const slot = noteToMacroSlot(data1)
    if (slot === undefined) return

    const isPress = status === MACRO_NOTE_ON && data2 > 0

    if (isPress) {
      midiState.pressedMacros.add(slot)
      if (!midiState.recording) triggerMacroSlot(slot)
    } else {
      midiState.pressedMacros.delete(slot)
    }
  }
}

// ====== Lights ====== //

/**
 * Computes which selector keys are represented in the current floor — same
 * feed calculation as useFeedsInFloor, but hook-free and index based. Used
 * for light feedback and to seed the selection when entering record mode.
 */
const computeFloorSelectors = (): Set<SelectorKey> => {
  const selectors = new Set<SelectorKey>()

  const { floor } = floorState
  const participants = getSortedHumanParticipants()

  if (!floor) return selectors

  switch (floor.layout) {
    case 'solo':
    case 'side-by-side': {
      const idx = participantIdToSpeakerIdx(floor.participantId)
      if (idx !== undefined) {
        if (floor.layout === 'side-by-side' || floor.identifier === 'camera')
          selectors.add(`speaker-${idx}`)
        if (floor.identifier === 'screenshare') selectors.add(`screen-${idx}`)
      }
      break
    }
    case 'tray':
      selectors.add('tray')
      break
    case 'panel': {
      participants.forEach((p, i) => {
        if (
          panelState.mode === 'auto' ||
          panelState.participantIds.includes(p.id)
        ) {
          selectors.add(`speaker-${i + 1}`)
        }
      })
      if (panelState.screen === 'tray') {
        selectors.add('tray')
      } else if (panelState.screen) {
        const idx = participantIdToSpeakerIdx(panelState.screen)
        if (idx !== undefined) selectors.add(`screen-${idx}`)
      }
      break
    }
    case 'custom': {
      const mix = mixesState.mixes.find((m) => m.id === floor.mixId)
      mix?.feeds.forEach((f) => {
        if (!f.media) return
        if (f.media.type === 'tray') {
          selectors.add('tray')
          return
        }
        const idx = participantIdToSpeakerIdx(f.media.id)
        if (idx === undefined) return
        if (f.media.type === 'screen') selectors.add(`screen-${idx}`)
        else selectors.add(`speaker-${idx}`)
      })
      break
    }
  }

  return selectors
}

const updateLightsState = () => {
  const lights: Record<string, boolean> = {}

  // in recording mode the lights reflect the locally stored selection,
  // otherwise they mirror what's currently in the floor
  const activeSelectors = midiState.recording
    ? midiState.recordedSelection
    : computeFloorSelectors()

  activeSelectors.forEach((sel) => {
    lights[sel] = true
  })

  // write full state (setting keys to false lets syncLightsMidi turn lights off)
  const allKeys = new Set([
    ...Object.keys(midiState.lights),
    ...Object.keys(lights),
  ])
  allKeys.forEach((key) => {
    const next = lights[key] ?? false
    if (midiState.lights[key] !== next) midiState.lights[key] = next
  })
}

const syncLightsMidi = (
  ops: Parameters<Parameters<typeof subscribe>[1]>[0]
) => {
  ops.forEach((op) => {
    const [action, path, value] = op
    const key = String(path[0])
    const note = selectorToNote(key)
    if (note === undefined) return

    if (action === 'set') {
      sendToOutputs(NOTE_ON, note, value ? VELOCITY_ON : VELOCITY_OFF)
    } else if (action === 'delete') {
      sendToOutputs(NOTE_ON, note, VELOCITY_OFF)
    }
  })
}

const useMidiEnabled = () => useSnapshot(midiState).enabled

export { toggleMidiControls, useMidiEnabled, midiState }
